import { clsx } from 'clsx'
import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'
import { FormValues } from '../../FormComponent/types'
import { StepAction, StepperComponentStepConfig } from '../types'

interface StepperStepContextValue {
  index: number
  currentStep: number
  step: StepperComponentStepConfig
  action: StepAction
  stepsLabel?: string
  markdownContent?: string
  isVertical: boolean
  stepData: FormValues
  canMarkAsComplete?: boolean
  changeStepsHandlerId?: string
  isActive: boolean
  contentClassName: string
}

type StepperStepContextProviderProps = {
  index: number
  currentStep: number
  step: StepperComponentStepConfig
  stepsLabel?: string
  markdownContent?: string
  isVertical: boolean
  stepData: FormValues
  canMarkAsComplete?: boolean
  changeStepsHandlerId?: string
  children: ReactNode
}

const StepperStepContext = createContext<StepperStepContextValue | null>(null)

export function StepperStepContextProvider({
  index,
  currentStep,
  step,
  stepsLabel,
  markdownContent,
  isVertical,
  stepData,
  canMarkAsComplete,
  changeStepsHandlerId,
  children,
}: StepperStepContextProviderProps) {
  const isActive = index === currentStep - 1
  const contentClassName = clsx('flex flex-col items-start gap-1.5 rounded-lg p-4 text-left', {
    '-translate-x-3': !isVertical,
    '-translate-y-2': isVertical,
    'bg-muted': isActive,
    'hover:bg-primary/10': isActive && !!step.action,
  })

  return (
    <StepperStepContext.Provider
      value={{
        index,
        currentStep,
        step,
        action: step.action,
        stepsLabel,
        markdownContent,
        isVertical,
        stepData,
        canMarkAsComplete,
        changeStepsHandlerId,
        isActive,
        contentClassName,
      }}
    >
      {children}
    </StepperStepContext.Provider>
  )
}

export function useStepperStepContext() {
  const context = useContext(StepperStepContext)
  if (!context) {
    throw new Error('useStepperStepContext must be used within StepperStepContextProvider')
  }
  return context
}
